import { Location } from "@/Icons/Location";
import { TenantsIcon } from "@/Icons/v2/SidebarIcons/Tenants";
import { useTerminalsState } from "@/States/Terminals";
import { getFormattedTerminalAddress } from "@/Utils/getFormattedTerminalAddress";
import { trpc } from "@/Utils/trpc";
import {
  type TimeFilter,
  defaultTimeFilter,
} from "@/components/v2/DatePicker/constants";
import { useTrans } from "@/translations";

import { useMemo } from "react";

import { BuildingLogoView } from "./BuildingLogoView";

type Props = {
  timeFilter?: TimeFilter;
};

export const HomeHeader: React.FC<Props> = ({ timeFilter }) => {
  const { t } = useTrans();
  const { currentTerminal } = useTerminalsState();

  const { data, isLoading: isLoadingData } =
    trpc.dwh.terminals.getById.useQuery(
      {
        ...(timeFilter || defaultTimeFilter),
        terminalId: currentTerminal?.id || "",
      },
      {
        queryKeyHashFn: () =>
          [
            "terminal",
            currentTerminal?.id,
            timeFilter?.startTimeISO,
            timeFilter?.endTimeISO,
          ].toString(),
        enabled: !!currentTerminal && !!timeFilter,
      },
    );

  const terminalName = useMemo(() => currentTerminal?.name, [currentTerminal]);

  const nrTenants = useMemo(
    () => data?.terminal.totalCustomersAcrossTerminal,
    [data],
  );

  const terminalAddress = useMemo(
    () => getFormattedTerminalAddress(currentTerminal),
    [currentTerminal],
  );

  if (!currentTerminal) {
    return null;
  }

  return (
    <div>
      <div className="align-center ml-[20px] flex">
        <div className="h-[76px] w-[76px]">
          <BuildingLogoView
            displayName={terminalName!}
            terminalId={currentTerminal?.id}
            size={76}
          />
        </div>
        <div className="ml-5 flex flex-col justify-center">
          <div className="font-signifier height-[42px] text-[40px] leading-[42px]">
            {terminalName}
          </div>
          <div className="mt-1">
            <div className="flex items-center text-[13.5px]">
              <div className="flex items-center">
                <Location />
                <div className="font-dm-sans text-grey6 ml-2">
                  {terminalAddress || "-"}
                </div>
                <div className="font-dm-sans text-grey4 ml-5">·</div>
              </div>
              {!isLoadingData && (
                <div className="ml-2 flex items-center">
                  <TenantsIcon size={14} stroke="#9A958F" />
                  <div className="font-dm-sans text-grey6 ml-2">
                    {nrTenants} {t("homePage:nrTenants")}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
